// @givmo/social — the composer a renter opens in place (SMM-WEB5; Social Media Manager Module plan
// §SMM-8, "SMM-WEB5 — the composer's embed export for renters"). Email Campaign's appeal tab
// (EMC-WEB5) draws Social's composer inside the tab — Jonathan's walkthrough frame 40 — and D-EMC-1
// rules that a rented surface feels part of its renter, never a jump to another module.
//
// The page's composer with no shell, laid out as frame 40's card (`ComposerCard`, `composer-card.tsx`):
// the same composer logic (`useComposer`, `composer-page.tsx`) and the same post view
// (`social-page.tsx`), so every rule, word and refusal is the page's. Only its layout and what
// happens around it differ:
//   * no header, no "Back to posts", no address of its own: after Save draft, Schedule or Publish
//     now it calls `onDone(post)` and never navigates; Cancel calls `onCancel`;
//   * the renter's campaign tags every new post (the context pair) and isn't repeated inside the
//     renter's own tab; the prefills (words, link, a suggested time) and the Collateral images the
//     renter names start the post, and the person confirms each;
//   * `postId` reopens a post in place: a draft (or a scheduled post taken back to a draft) in the
//     composer, anything else read-only with the acts on it;
//   * a new post's composer starts afresh once its post is saved, scheduled or published: the post is
//     the renter's to list and reopen (`postId`), so a renter that keeps the card showing never
//     writes the next post over the last one.
//   * before any account is live, a new post's place shows frame 39's connect step instead
//     (`EmbedConnectStep`): one row per platform the organization may connect, coming back here.
//
// It checks nothing about the module's offering: the renter shows it only where Social is offered
// (`rentable`). It renders beneath the console's QueryClientProvider, router and configured api-client
// (each console has all three at its root) and brings none of its own, so its reads and writes share
// the console's cache and its sign-out boundary.

import { useState, type ReactNode } from 'react';
import type { SocialPostOut } from '@givmo/api-client';
import { Button, testIdProp } from '@givmo/design-system';
import { channelsOf, type ComposerContext } from './composer-model';
import { ComposerCard } from './composer-card';
import type { ComposerEntry, ComposerHost } from './composer-page';
import { EmbedConnectStep } from './embed-connect-step';
import { canPublish, canRead, type SocialConfig } from './social-config';
import { PostView, type SocialFrame } from './social-page';
import { linkProblem } from './social-platforms';
import { useConnections } from './social-queries';
import { SocialLoading, SocialStateCard } from './social-ui';
import { socialWords as w } from './social-words';

/** The root testid of a renter's composer. */
export const SOCIAL_COMPOSER_EMBED_TEST_ID = 'social-composer-embed';

/** The campaigns a renter may tag a post with (the backend's `context_kind`). */
export type SocialContextKind = 'fundraiser_campaign';

/** What a renter starts a post with: each a prefill the person confirms, never a send. */
export interface SocialComposerPrefill {
  /** A new post's opening words. */
  text?: string | null;
  /** A new post's link (the appeal's share link): offered only when it is a valid https link. */
  link?: string | null;
  /** Which of the organization's current Collateral images to offer, in this order. Absent offers
   *  every current image; an empty list offers none. Nothing is copied until the person picks one. */
  collateralItemIds?: readonly string[] | null;
  /** A suggested time, ISO 8601: where the card's date and time start while it is inside the
   *  window. A reopened post's own earlier time comes first. */
  suggestedAt?: string | null;
}
// Each prefill may arrive after the card mounts (a renter's own reads settle later): the card takes
// it while the person hasn't written that part of the new post (or picked a date or time) themselves.

export interface SocialComposerEmbedProps {
  /** The organization. Render the embed only once it is known. */
  orgRef: string;
  /** The organization's name as its console shows it, for the card's Instagram bio hint ("Add the
   *  link to {name}'s Instagram bio"); without it the hint says "your". */
  orgName?: string | null;
  /** The organization's time zone (IANA, `America/New_York`), so the card's date and time, the
   *  suggestion and the readback are on the clock the renter's own list shows ("9:00 AM Eastern
   *  Time"), whatever zone the person is in. Without it (or one the browser doesn't know), the
   *  person's own zone. */
  timeZone?: string | null;
  /** The person's effective permissions from the host's ONE standing read, `null` while it is
   *  unsettled — the set Social's own page gets. Writing needs `social:publish`; reconnecting an
   *  account, `social:manage_connections`. */
  permissions: readonly string[] | null;
  /** The renter's campaign: every new post is tagged with it. */
  context?: { kind: SocialContextKind; id: string } | null;
  prefill?: SocialComposerPrefill | null;
  /** A post to reopen in place: a draft or a scheduled post to edit, or any other to read. */
  postId?: string | null;
  /** After Save draft, Schedule or Publish now, with the post as the server answered it. A new
   *  post's card then starts afresh: the saved post is the renter's to list and reopen. */
  onDone: (post: SocialPostOut) => void;
  /** The way out without saving: Cancel beside the composer's acts (Close on a read-only post). */
  onCancel?: () => void;
  /** After a draft is deleted. Delete draft is offered only when this is given. */
  onDeleted?: (postId: string) => void;
}

/** The renter's prefills as the composer's entry, read tolerantly: a link that isn't valid https
 *  or a time that doesn't parse is simply not offered (the deep link's own rule). */
function embedEntry(
  context: ComposerContext | null,
  prefill: SocialComposerPrefill | null,
): ComposerEntry {
  const link = prefill?.link?.trim() ?? '';
  const at = prefill?.suggestedAt ? new Date(prefill.suggestedAt) : null;
  return {
    context,
    text: prefill?.text ?? null,
    link: link.length > 0 && linkProblem(link) === null ? link : null,
    suggestedAt: at !== null && !Number.isNaN(at.getTime()) ? at.toISOString() : null,
    collateralItemIds: prefill?.collateralItemIds ?? null,
  };
}

/** A scheduled post taken back to a draft stays where it is: as the post reads again, its view
 *  turns into the composer. */
const stayInPlace = () => undefined;

export function SocialComposerEmbed({
  orgRef,
  orgName = null,
  timeZone = null,
  permissions,
  context = null,
  prefill = null,
  postId = null,
  onDone,
  onCancel,
  onDeleted,
}: SocialComposerEmbedProps) {
  const config: SocialConfig = { orgRef, permissions, orgName, timeZone };
  const framed: ComposerContext | null = context
    ? { kind: context.kind, ref: context.id, label: null }
    : null;
  const entry = embedEntry(framed, prefill);
  // The organization's accounts: before any is live, a new post's place is frame 39's connect step.
  // (The card's own read of them is this same query.)
  const connections = useConnections(postId === null && canPublish(config) ? orgRef : null);
  const accounts = channelsOf(connections.data ?? []);
  const live = accounts.some((account) => !account.needsReconnect);
  // A new post's composer, afresh after each post it saves, schedules or publishes (or deletes):
  // kept bound to a saved draft, the card would write the next post over it.
  const [round, setRound] = useState(0);
  const afresh = () => {
    if (postId === null) setRound((count) => count + 1);
  };
  const host: ComposerHost = {
    onSaved: (post) => {
      onDone(post);
      afresh();
    },
    onDeleted: onDeleted
      ? (deleted) => {
          onDeleted(deleted);
          afresh();
        }
      : undefined,
    // Back to the page the person is on, the renter's tab, whose list holds the saved draft.
    reconnectReturn: () => null,
    framedContext: framed,
    onCancel,
  };
  // A post's read-only view (and its loading and refusal), bare: the renter's Close on the left,
  // apart from the post's own acts on the right (its Cancel takes the post off the schedule).
  const frame: SocialFrame = ({ actions, children }) => (
    <div className="flex flex-col gap-16">
      {children}
      {actions || onCancel ? (
        <div className="flex flex-wrap items-center gap-8">
          {onCancel ? (
            <Button
              appearance="saas"
              tone="secondary"
              onClick={onCancel}
              testId="social-embed-close"
            >
              {w.close}
            </Button>
          ) : null}
          {actions ? <div className="ml-auto">{actions}</div> : null}
        </div>
      ) : null}
    </div>
  );

  let body: ReactNode;
  if (!canRead(config)) {
    body = (
      <SocialStateCard
        testId="social-no-access"
        title={w.noAccessTitle}
        description={w.noAccessBody}
      />
    );
  } else if (postId !== null) {
    body = (
      <PostView
        config={config}
        postId={postId}
        frame={frame}
        compact
        onEdited={stayInPlace}
        composer={(post, keptTime) => (
          <ComposerCard
            key={post.post_id}
            config={config}
            post={post}
            entry={{ ...entry, suggestedAt: keptTime ?? entry.suggestedAt }}
            host={host}
          />
        )}
      />
    );
  } else if (!canPublish(config)) {
    body = (
      <SocialStateCard
        testId="social-no-post-access"
        title={w.noPostAccessTitle}
        description={w.noPostAccessBody}
      />
    );
  } else if (connections.isPending) {
    body = <SocialLoading>{w.loadingAccounts}</SocialLoading>;
  } else if (connections.isSuccess && !live) {
    body = (
      <EmbedConnectStep
        config={config}
        lapsed={accounts.filter((account) => account.needsReconnect)}
      />
    );
  } else {
    body = <ComposerCard key={round} config={config} entry={entry} host={host} />;
  }
  return (
    <div className="flex min-w-0 flex-col" {...testIdProp(SOCIAL_COMPOSER_EMBED_TEST_ID)}>
      {body}
    </div>
  );
}
